<template>
<div>
    <p>
        With this utility you can mass convert "local" phone numbers to international format.
    </p>
    <div>
        <div class="q-mb-md">
            <q-btn
                class="q-mr-md"
                label="Refresh"
                :loading="loading"
                @click="loadFromServer"
            />
            <q-btn
                class="q-mr-md"
                label="Accept suggested"
                color="primary"
                :loading="loading"
                @click="accept"
                :disable="! selected.length"
            />
            <span v-if="bulkCount > 0 && accepted > 0 && accepted < bulkCount && loading">
                Processed {{ accepted }} of {{ bulkCount }}
            </span>
        </div>

        <q-table
            :rows="rows"
            :columns="columns"
            row-key="id"
            :rows-per-page-options="[15, 35, 50, 100]"
            flat
            selection="multiple"
            v-model:selected="selected"
            :loading="loading"
            :sort-method="customSort"
            no-data-label="No phone numbers were found for modification"
            >
            <template #body-cell-phone="props">
                <q-td :props="props">
                    <div>
                        {{ props.row.phone }}
                    </div>
                    <div v-for="match in props.row.matches">
                        <q-icon
                            class="q-mr-sm"
                            color="orange"
                            name="assistant"
                            size="16px"
                            v-if="props.row.preferredMatch && match.country_code === props.row.preferredMatch.country_code"
                        >
                            <q-tooltip
                                class="text-body2"
                            >
                                Preferred match (based on customer's country)
                            </q-tooltip>
                        </q-icon>
                        <strong>{{ match.country_code }}</strong>{{ props.row.phone }} ({{ match.country_iso }})
                    </div>
                </q-td>
            </template>
            <template #body-cell-type="props">
                <q-td :props="props">

                    <div v-if="props.row.processed">
                        <q-icon name="check" color="green" size="sm" class="q-ml-md" />
                    </div>
                    <div v-else>
                        <div
                            v-for="match in props.row.matches"
                        >
                            <q-btn
                                class="q-mb-sm"
                                dense
                                :loading="props.row.processing"
                                :flat="match.country_iso !== props.row.preferredMatch?.country_iso"
                                :label="`Accept ${match.country_iso} (${match.phone_type})`"
                                color="primary"
                                no-caps
                                @click="acceptCandidate(props.row, match.country_code, match.phone_type)"
                            />
                        </div>
                    </div>
                </q-td>
            </template>
            <template #body-cell-details="props">
                <q-td :props="props">
                    <div>
                        <strong>{{ props.row.name }}</strong>
                    </div>
                    <div>
                        {{ props.row.email }}
                    </div>
                    <div>
                        <span v-if="props.row.country">{{ props.row.country }}, </span>
                        <span v-if="props.row.city">{{ props.row.city }}, </span>
                        {{ props.row.address }}
                    </div>
                    <div>
                        {{ props.row.connection }}
                    </div>
                </q-td>
            </template>
            <template #body-cell-actions="props">
                <q-td :props="props">
                    <div v-if="props.row.processed">
                        <q-icon name="check" color="green" size="sm" class="q-ml-md" />
                    </div>
                    <div v-else>
                        <q-btn
                            v-if="props.row.matches.length === 1"
                            dense
                            outline
                            :loading="props.row.processing"
                            :label="`Accept ${props.row.preferredMatch.country_iso}`"
                            color="primary"
                            @click="acceptCandidate(props.row, props.row.preferredMatch.country_code, props.row.preferredMatch.phone_type)"
                        />
                        <div v-if="props.row.matches.length > 1">
                            <div
                                v-for="match in props.row.matches"
                            >
                                <q-btn
                                    class="q-mb-sm"
                                    dense
                                    :loading="props.row.processing"
                                    :outline="match.country_iso === props.row.preferredMatch?.country_iso"
                                    :flat="match.country_iso !== props.row.preferredMatch?.country_iso"
                                    :label="`Accept ${match.country_iso}`"
                                    color="primary"
                                    @click="acceptCandidate(props.row, match.country_code, match.phone_type)"
                                />
                            </div>
                        </div>
                    </div>
                </q-td>
            </template>
        </q-table>
    </div>
</div>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {Notify} from "quasar";
import useCountryPrefixes from "../composables/useCountryPrefixes";

/**
 * @typedef {{
 * country: string,
 * country_iso: string,
 * country_code: string,
 * phone_type: string
 * }} Match
 */

/**
 * @typedef {{
 *   id: number,
 *   first_name: string,
 *   last_name: string,
 *   honorific: string,
 *   country: string,
 *   city: string,
 *   address: string,
 *   connection: string,
 *   phone: string,
 *   email: string
 * }} Contact
 */

/**
 * @typedef {{
 *   id: number,
 *   contact: Contact,
 *   type: string,
 *   number: string
 * }} CandidateData
 */

const loading = ref(false)
const columns = ref([
    {
        name: 'phone',
        label: 'Phone',
        field: 'phone',
        align: 'right',
    },
    {
        name: 'type',
        label: 'Type',
        field: 'type',
        align: 'left',
        sortable: false,
    },
    {
        name: 'details',
        label: 'Contact',
        align: 'left',
        sortable: true,
    },
])

const rows = ref([])

/**
 * @type {ToRef<Candidate[]>}
 */
const selected = ref([])

class Candidate {
    /**
     * @property {Match[]} matches
     * @property {Match|null} preferredMatch
     */

    /**
     * @param {CandidateData} row
     */
    constructor(row) {

        this.id = row.id
        this.name = (row.contact.honorific || '') + ' ' + row.contact.first_name + ' ' + row.contact.last_name
        this.email = row.contact.email
        this.country = row.contact.country
        this.city = row.contact.city
        this.address = row.contact.address
        this.connection = row.contact.connection
        this.phone = row.number
        this.type = row.type
        this.contact_id = row.contact.id
        this.matches = findCodeByRegex(row.number)
        this.preferredMatch = null
        this.processing = false
        this.processed = false
        if(this.matches.length === 1) {
            this.preferredMatch = this.matches[0]
        }
        if(this.matches.length > 1) {
            this.preferredMatch = this.matches.find((match) => {
                return match.country_iso === this.country || match.country === this.country
            })
        }
    }
}

/**
 * @typedef {Person & { preferredMatch: Match }} CandidateWithPreferred
 */

const loadFromServer = async () => {
    loading.value = true
    window['APIv2']._request('phones-prefixes/candidates', {
        query: "",
        workspace_id: window['APIv2'].workspace.id,
    }).then((response) => {
        rows.value = response.items.filter(row => !!row.contact).map((row) => {
            return new Candidate(row)
        }).filter((candidate) => {
            return candidate.id !== null && candidate.matches.length > 0
        })

    }).catch((err) => {console.log(err)})
        .finally(() => {
            loading.value = false
        })
}

const { regexMap } = useCountryPrefixes()

/**
 * @param {string} value
 * @return {Match[]}
 */
function findCodeByRegex(value) {
    const matches = []
    for (let i = 0; i < regexMap.length; i++) {
        const country = regexMap[i]
        for (let j = 0; j < country.country_regexes.length; j++) {
            const regex = country.country_regexes[j]
            if (regex.test(value)) {
                matches.push({
                    'country': country.country,
                    'country_iso': country.country_iso,
                    'country_code': country.country_code,
                    'phone_type': country.phone_type,
                })
            }
        }
    }
    return matches
}

const accepted = ref(0)
const bulkCount = ref(0)
const accept = async () => {
    accepted.value = 0
    loading.value = true

    /**
     * @type {CandidateWithPreferred[]}
     */
    const singleMatched = selected.value.filter((candidate) => {
        return !! candidate.preferredMatch
    })

    bulkCount.value = singleMatched.length
    selected.value = []

    for (let i = 0; i < singleMatched.length; i++) {

        try {
            await acceptCandidate(singleMatched[i], singleMatched[i].preferredMatch.country_code, singleMatched[i].preferredMatch.phone_type)
        } catch (e) {
            console.log(e)
            Notify.create({
                message: `Failed to save ${singleMatched[i].phone}`,
                color: 'negative',
                position: 'top',
                timeout: 2000,
            })
        }
        accepted.value++
    }

    loading.value = false
    Notify.create({
        message: `Saved ${accepted.value} numbers`,
        color: 'positive',
        position: 'top',
        timeout: 2000,
    })
    loadFromServer()
}

const acceptCandidate = (candidate, country_code, type) => {
    candidate.processing = true
    return window['APIv2'].requestWithAxios(`phones-prefixes/fix?workspace_id=${window['APIv2'].workspace.id}`,
{
        id: candidate.id,
        phone: country_code + candidate.phone,
        contact_id: candidate.contact_id,
        type: type,
    }, 'POST')
        .then((response) => {
            candidate.processed = true
        })
        .finally(() => {
            candidate.processing = false
        })
}

function customSort (rows, sortBy, descending) {

    const data = [...rows]

    if (sortBy) {
        data.sort((a, b) => {
            const x = descending ? b : a
            const y = descending ? a : b

            if (sortBy === 'details') {
                // sort by country
                return x.country.localeCompare(y.country)
            }
            else {
                // numeric sort
                return parseFloat(x[ sortBy ]) - parseFloat(y[ sortBy ])
            }
        })
    }

    return data
}

onMounted(() => {
    if(window['APIv2']){
        loadFromServer()
    } else {
        window.addEventListener('APIv2Ready', () => {
            loadFromServer()
        })
    }
})
</script>

<style scoped>

</style>
